import { StatusChip } from "$components/chip";
import {
  Table,
  type TableColumnDefinition,
  type TableRowDefinition,
} from "$components/table";
import { DifferenceChip } from "$routes/inventory/_lib/difference-chip";
import { Box, makeStyles, Typography } from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import type { ValidateStockTakeBulkUploadQuery } from "./__generated__/bulk-stocktake-operations.gql";

export type StockTakeBulkRowValidation = NonNullable<
  ValidateStockTakeBulkUploadQuery["validateStockTakeBulkUpload"]
>["rows"][number];

const useStyles = makeStyles((theme) => ({
  errorRow: {
    backgroundColor: theme.palette.error.light,
  },
}));

interface Props {
  rows: readonly StockTakeBulkRowValidation[];
}

const RowStatus: FC<{ row: StockTakeBulkRowValidation }> = ({ row }) => {
  if (row.hasError) {
    return (
      <StatusChip
        status="invalid"
        label={
          row.errorMessage ?? (
            <FormattedMessage
              id="inventory.bulkStocktake.status.error"
              defaultMessage="Error"
            />
          )
        }
      />
    );
  }

  if (row.willUpdate) {
    return (
      <StatusChip
        status="valid"
        label={
          <FormattedMessage
            id="inventory.bulkStocktake.status.update"
            defaultMessage="Will update"
          />
        }
      />
    );
  }

  return (
    <StatusChip
      status="disabled"
      label={
        <FormattedMessage
          id="inventory.bulkStocktake.status.unchanged"
          defaultMessage="No change"
        />
      }
    />
  );
};

export const StockTakeBulkPreviewTable: FC<Props> = ({ rows }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const columns: TableColumnDefinition[] = [
    {
      field: "skuName",
      headerName: formatMessage({
        id: "inventory.bulkStocktake.column.product",
        defaultMessage: "Product",
      }),
      flex: 2,
    },
    {
      field: "expiryDate",
      headerName: formatMessage({
        id: "inventory.bulkStocktake.column.expiry",
        defaultMessage: "Expiry",
      }),
      flex: 1,
    },
    {
      field: "systemQuantity",
      headerName: formatMessage({
        id: "inventory.bulkStocktake.column.system",
        defaultMessage: "System",
      }),
      flex: 0.7,
      alignContents: "right",
    },
    {
      field: "actualCount",
      headerName: formatMessage({
        id: "inventory.bulkStocktake.column.counted",
        defaultMessage: "Counted",
      }),
      flex: 0.7,
      alignContents: "right",
    },
    {
      field: "discrepancy",
      headerName: formatMessage({
        id: "inventory.bulkStocktake.column.difference",
        defaultMessage: "Difference",
      }),
      flex: 0.8,
      renderCell: (_value, _index, _col, row) => {
        const original = row.original as StockTakeBulkRowValidation;
        return original.discrepancy === null ||
          original.discrepancy === undefined ? null : (
          <DifferenceChip difference={original.discrepancy} />
        );
      },
    },
    {
      field: "status",
      headerName: formatMessage({
        id: "inventory.bulkStocktake.column.status",
        defaultMessage: "Status",
      }),
      flex: 1.5,
      renderCell: (_value, _index, _col, row) => {
        const original = row.original as StockTakeBulkRowValidation;
        return (
          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <RowStatus row={original} />
            {original.modifiedSinceExport && (
              <Typography variant="caption" color="textSecondary">
                <FormattedMessage
                  id="inventory.bulkStocktake.status.staleRow"
                  defaultMessage="Changed since export"
                />
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  const tableRows: TableRowDefinition[] = rows.map((row) => ({
    id: row.rowIndex,
    original: row,
    skuName: row.skuName ?? "",
    expiryDate: row.expiryDate ?? "",
    systemQuantity: row.systemQuantity ?? "",
    actualCount: row.actualCount ?? "",
  }));

  return (
    <Table
      columns={columns}
      rows={tableRows}
      dataTestId="bulk-stocktake-preview-table"
      classes={{
        rowClass: (row) => {
          const original = row.original as StockTakeBulkRowValidation;
          return original.hasError ? classes.errorRow : "";
        },
      }}
    />
  );
};
